import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Timeline as MovementsIcon,
  PlayArrow as RunIcon,
  TableChart as ExcelIcon,
  HelpOutline as CostHelpIcon,
  KeyboardArrowRight as CollapsedIcon,
  KeyboardArrowDown as ExpandedIcon,
} from '@mui/icons-material';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import inventoryReportService from '../../../../services/inventoryReportService';
import type {
  InventoryMovementsReport as ReportData,
  InventoryMovementsReportRowLocation,
} from '../../../../services/inventoryReportService';
import venueLocationService from '../../../../services/venueLocationService';
import inventoryCategoryService from '../../../../services/inventoryCategoryService';
import type { VenueLocation, InventoryCategory, InventorySubcategory } from '../../../../types/venue';
import DateRangeFilter from '../../../../components/venue/DateRangeFilter';
import ColumnVisibilityMenu from '../../../../components/venue/ColumnVisibilityMenu';
import PageHeader from '../../../../components/venue/PageHeader';
import FilterBar from '../../../../components/venue/FilterBar';
import EmptyState from '../../../../components/venue/EmptyState';
import InventoryMovementsReportHelpModal from '../../../../components/venue/InventoryMovementsReportHelpModal';
import CostTrackingHelpModal from '../../../../components/venue/CostTrackingHelpModal';
import { downloadInventoryMovementsReport } from '../../../../utils/inventoryMovementsReportTemplate';
import { formatQty } from '../../../../utils/quantityFormat';
import { fromMinorUnitsOrNull } from '../../../../utils/money';
import { sizeCaption } from '../../../../utils/inventoryItemDisplay';
import { useColumnVisibility } from '../../../../utils/columnVisibility';
import type { ColumnDef } from '../../../../utils/columnVisibility';

// ── Table columns ─────────────────────────────────────────────────────────────
// Fixed widths (table-layout: fixed); the width-less Item column absorbs the
// remainder so names can't bleed into the numeric columns. All columns except
// Item are user-hideable via the Columns menu; exports stay full regardless.
//
// REPORTING-CENTER.2.2: same principle as the register — trim the roomy
// non-Item columns (Category + the single-word numeric buckets) so Item gains
// space, while keeping enough width that the longer headers never clip.
type MovColMeta = ColumnDef & {
  width?: number;
  align?: 'right';
  /** MONEY column — filtered out entirely for users without stock-costs:view. */
  money?: boolean;
  /** Ships toggled off (the pre-HOTFIX-UI-3 default view stays unchanged). */
  defaultOff?: boolean;
};

const MOVEMENTS_COLUMNS: MovColMeta[] = [
  { key: 'item',            label: 'Item',          locked: true },
  { key: 'category',        label: 'Category',      width: 146 },
  // HOTFIX-UI-3 (fix 4) — three new toggles, default OFF. Supplier is
  // operational (all roles); the two price columns are money-gated.
  { key: 'supplier',        label: 'Supplier',      width: 140, defaultOff: true },
  { key: 'opening',         label: 'Opening',       width: 88,  align: 'right' },
  { key: 'received',        label: 'Received',      width: 88,  align: 'right' },
  { key: 'transferIn',      label: 'Transfer In',   width: 98,  align: 'right' },
  { key: 'adjustmentPlus',  label: 'Adj +',         width: 80,  align: 'right' },
  { key: 'salesDepletion',  label: 'Sales / Depl.', width: 106, align: 'right' },
  { key: 'waste',           label: 'Waste',         width: 80,  align: 'right' },
  { key: 'transferOut',     label: 'Transfer Out',  width: 100, align: 'right' },
  { key: 'adjustmentMinus', label: 'Adj −',         width: 80,  align: 'right' },
  { key: 'reversalVoid',    label: 'Reversal',      width: 90,  align: 'right' },
  { key: 'net',             label: 'Net',           width: 88,  align: 'right' },
  { key: 'closing',         label: 'Closing',       width: 90,  align: 'right' },
  { key: 'unitCost',        label: 'Cost price',    width: 100, align: 'right', money: true, defaultOff: true },
  { key: 'salePrice',       label: 'Selling price', width: 108, align: 'right', money: true, defaultOff: true },
  { key: 'closingValue',    label: 'Closing Value', width: 118, align: 'right', money: true },
];

// Defaults: every column visible except the defaultOff ones (HOTFIX-UI-3 keeps
// the pre-existing default view). Memoized once (no venue-conditional defaults
// here, unlike the register's location column).
const MOVEMENTS_COLUMN_DEFAULTS: Record<string, boolean> = MOVEMENTS_COLUMNS
  .reduce((acc, c) => { acc[c.key] = !c.defaultOff; return acc; }, {} as Record<string, boolean>);

/**
 * REPORTING-CENTER.1 — Inventory Movements & Stock on Hand report page.
 *
 * Date-range report over the movement ledger: opening (ledger sum before the
 * range) → period buckets → closing, per item. Consumes
 * GET /admin/venue/:venueId/inventory/reports/movements and renders the
 * summary on-screen; the 3-sheet XLSX download includes the movement detail.
 */

const isoDay = (d: Date): string => d.toISOString().slice(0, 10);

/** Expand YYYY-MM-DD inputs to a full-day inclusive ISO range. */
const dayStart = (day: string): string => `${day}T00:00:00.000Z`;
const dayEnd = (day: string): string => `${day}T23:59:59.999Z`;

const fmtMoney = (n: number | null): string =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const InventoryMovementsReport: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useVenue();
  const { canViewStockCosts } = useAuth();
  const showCost = canViewStockCosts(); // COSTING.1d — hide the value column if not entitled

  // ── Filters ────────────────────────────────────────────────────────────────
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [fromDay, setFromDay] = useState(isoDay(monthAgo));
  const [toDay, setToDay] = useState(isoDay(today));
  const [locationFilter, setLocationFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState('');

  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [subcategories, setSubcategories] = useState<InventorySubcategory[]>([]);

  // ── Report state ───────────────────────────────────────────────────────────
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  /**
   * REPORTING.R5 — which rows have their location breakdown open.
   *
   * Keyed by inventoryItemId rather than row index so it survives a re-sort,
   * and cleared whenever a new report arrives: an expanded row from the
   * previous range would otherwise reopen against different numbers.
   */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // COST-GUIDE.1 — separate affordance for the cost guide (how values are derived).
  const [costHelpOpen, setCostHelpOpen] = useState(false);

  // Column visibility (persisted per venue). Item is locked; the rest hideable.
  const { visible, toggle, reset } = useColumnVisibility(
    'movementsReport.cols', venueId, MOVEMENTS_COLUMNS, MOVEMENTS_COLUMN_DEFAULTS,
  );
  const activeColumns = useMemo<MovColMeta[]>(
    () => MOVEMENTS_COLUMNS.filter(c => (showCost || !c.money) && (c.locked || visible[c.key])),
    [visible, showCost],
  );
  const tableMinWidth = activeColumns.reduce((sum, c) => sum + (c.width ?? 240), 0);

  // Load filter option lists once (non-blocking, like InventoryValuation/Dashboard).
  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId).then(setLocations).catch(() => { /* non-blocking */ });
    inventoryCategoryService.getCategories(venueId, {}).then(setCategories).catch(() => { /* non-blocking */ });
  }, [venueId]);

  // Subcategories follow the selected category; changing category resets the pick.
  useEffect(() => {
    setSubcategoryFilter('');
    if (!venueId || !categoryFilter) { setSubcategories([]); return; }
    inventoryCategoryService.getSubcategories(venueId, categoryFilter, {})
      .then(setSubcategories)
      .catch(() => setSubcategories([]));
  }, [venueId, categoryFilter]);

  const runReport = useCallback(async () => {
    if (!venueId || !fromDay || !toDay) return;
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryReportService.getMovementsReport(venueId, {
        from: dayStart(fromDay),
        to: dayEnd(toDay),
        locationId: locationFilter || undefined,
        categoryId: categoryFilter || undefined,
        subcategoryId: subcategoryFilter || undefined,
      });
      setReport(result);
      // REPORTING.R5 — a row left expanded from the previous run would reopen
      // against a different date range's numbers.
      setExpanded(new Set());
    } catch (err: any) {
      setReport(null);
      setExpanded(new Set());
      setError(err?.response?.data?.error?.message ?? 'Failed to build the report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [venueId, fromDay, toDay, locationFilter, categoryFilter, subcategoryFilter]);

  // Initial run with the default 30-day range (house pages load on mount).
  useEffect(() => {
    if (!venueId) return;
    runReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);

  const qtyCell = (key: string, n: number, opts: { dim?: boolean; signed?: boolean } = {}) => (
    <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap', color: opts.dim && n === 0 ? theme.palette.text.disabled : undefined }}>
      {opts.signed && n > 0 ? `+${formatQty(n)}` : formatQty(n)}
    </TableCell>
  );

  const toggleRow = (itemId: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
    return next;
  });

  /**
   * REPORTING.R5 — one location's share of the row above it.
   *
   * Deliberately quiet: no bold, indented under the item, and the MONEY columns
   * are left EMPTY rather than filled. The server splits quantities per
   * location and not value, and a blank cell says "not calculated" where a
   * repeated or apportioned figure would be an invention.
   */
  const locationSubRow = (
    itemId: string,
    loc: InventoryMovementsReportRowLocation,
  ) => {
    const cell = (col: MovColMeta): React.ReactNode => {
      switch (col.key) {
        case 'item':
          return (
            <TableCell key="item" sx={{ pl: 5, borderBottom: 'none', py: 0.5 }}>
              <Typography variant="caption" color="text.secondary" noWrap title={loc.locationName ?? undefined}>
                {loc.locationName ?? 'No location'}
              </Typography>
            </TableCell>
          );
        case 'opening':         return subQty('opening', loc.openingQuantity);
        case 'received':        return subQty('received', loc.received);
        case 'transferIn':      return subQty('transferIn', loc.transferIn);
        case 'adjustmentPlus':  return subQty('adjustmentPlus', loc.adjustmentPlus);
        case 'salesDepletion':  return subQty('salesDepletion', loc.salesDepletion);
        case 'waste':           return subQty('waste', loc.waste);
        case 'transferOut':     return subQty('transferOut', loc.transferOut);
        case 'adjustmentMinus': return subQty('adjustmentMinus', loc.adjustmentMinus);
        case 'reversalVoid':    return subQty('reversalVoid', loc.reversalVoid);
        case 'net':             return subQty('net', loc.netChange, true);
        case 'closing':         return subQty('closing', loc.closingQuantity);
        default:
          // Category, supplier and every money column: blank on a breakdown line.
          return <TableCell key={col.key} sx={{ borderBottom: 'none', py: 0.5 }} />;
      }
    };
    return (
      <TableRow key={`${itemId}:${loc.locationId ?? 'null'}`} sx={{ bgcolor: theme.palette.action.hover }}>
        {activeColumns.map(cell)}
      </TableRow>
    );
  };

  const subQty = (key: string, n: number, signed = false) => (
    <TableCell
      key={key}
      align="right"
      sx={{
        whiteSpace: 'nowrap', borderBottom: 'none', py: 0.5,
        color: n === 0 ? theme.palette.text.disabled : theme.palette.text.secondary,
      }}
    >
      <Typography variant="caption">
        {signed && n > 0 ? `+${formatQty(n)}` : formatQty(n)}
      </Typography>
    </TableCell>
  );

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        icon={<MovementsIcon />}
        title={<>Inventory Movements &amp; Stock on Hand</>}
        onHelp={() => setHelpOpen(true)}
        helpTitle="How the numbers are computed"
        subtitle="Opening stock, period movements, and closing stock per item — computed from the movement ledger for any date range."
      />

      {/* ── Filter bar ─────────────────────────────────────────────────────── */}
      <FilterBar
        actionsLeft={
          <>
            <ColumnVisibilityMenu columns={MOVEMENTS_COLUMNS} visible={visible} onToggle={toggle} onReset={reset} disabled={loading} />
            {/* COST-GUIDE.1 — how Closing Value is derived; only for users who see cost. */}
            {showCost && (
              <Tooltip title="How cost tracking works" arrow>
                <IconButton size="small" onClick={() => setCostHelpOpen(true)} sx={{ color: 'text.secondary' }}>
                  <CostHelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
        actions={
          <>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
              onClick={runReport}
              disabled={loading || !fromDay || !toDay}
            >
              Run
            </Button>
            <Button
              variant="outlined"
              startIcon={<ExcelIcon />}
              onClick={() => report && downloadInventoryMovementsReport(report)}
              disabled={!report || loading || report.rows.length === 0}
            >
              Download XLSX
            </Button>
          </>
        }
      >
        <DateRangeFilter from={fromDay} to={toDay} onFromChange={setFromDay} onToChange={setToDay} disabled={loading} />

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Location</InputLabel>
          <Select value={locationFilter} label="Location" onChange={e => setLocationFilter(e.target.value)} disabled={loading}>
            <MenuItem value="">All locations</MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc._id} value={loc._id}>{loc.displayName ?? loc.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Category</InputLabel>
          <Select value={categoryFilter} label="Category" onChange={e => setCategoryFilter(e.target.value)} disabled={loading}>
            <MenuItem value="">All categories</MenuItem>
            {categories.map(cat => (
              <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 170 }} disabled={!categoryFilter || loading}>
          <InputLabel>Subcategory</InputLabel>
          <Select value={subcategoryFilter} label="Subcategory" onChange={e => setSubcategoryFilter(e.target.value)}>
            <MenuItem value="">All subcategories</MenuItem>
            {subcategories.map(sub => (
              <MenuItem key={sub._id} value={sub._id}>{sub.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

      </FilterBar>

      {/* ── Truncation warning ─────────────────────────────────────────────── */}
      {report?.meta.truncated && (
        <Alert severity="warning">
          The movement detail was truncated: this range has {report.meta.movementCount.toLocaleString()} movements and only
          the first {report.movements?.length.toLocaleString()} are included in the download. Summary totals are complete.
          Narrow the date range or filters for full detail.
        </Alert>
      )}

      {/* ── States + table ─────────────────────────────────────────────────── */}
      {error && <Alert severity="error">{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {!loading && !error && report && report.rows.length === 0 && (
        <EmptyState
          icon={MovementsIcon}
          message="No stock or movements in this period. Adjust the date range or filters and run again."
        />
      )}

      {!loading && report && report.rows.length > 0 && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', gap: 2, alignItems: 'baseline' }}>
            <Typography fontWeight={700}>{report.rows.length} items</Typography>
            <Typography variant="caption" color="text.secondary">
              {report.meta.movementCount.toLocaleString()} movements · {fromDay} → {toDay}
              {report.meta.locationName ? ` · ${report.meta.locationName}` : ' · all locations'}
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }}>
              <colgroup>
                {activeColumns.map(c => <col key={c.key} style={c.width ? { width: c.width } : undefined} />)}
              </colgroup>
              <TableHead>
                <TableRow>
                  {activeColumns.map(c => (
                    <TableCell key={c.key} align={c.align}>{c.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {report.rows.map(row => {
                  const size = sizeCaption(row.itemName, row.sizeValue, row.sizeUnit);
                  // Only a row whose stock actually sits in more than one place
                  // has a breakdown worth opening.
                  const splittable = row.byLocation.length > 1;
                  const isOpen = splittable && expanded.has(row.inventoryItemId);
                  const renderCell = (col: MovColMeta): React.ReactNode => {
                    switch (col.key) {
                      case 'item':
                        return (
                          <TableCell key="item" sx={{ pl: splittable ? 0.5 : undefined }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {/*
                                REPORTING.R5 — only offered where there is
                                something to see. A single-location row has
                                nothing to decompose, and a chevron that expands
                                into one line repeating the row is noise on every
                                row of a single-venue install.
                              */}
                              {splittable ? (
                                <IconButton
                                  size="small"
                                  onClick={() => toggleRow(row.inventoryItemId)}
                                  aria-label={isOpen ? 'Hide locations' : 'Show locations'}
                                  aria-expanded={isOpen}
                                  sx={{ p: 0.25 }}
                                >
                                  {isOpen ? <ExpandedIcon fontSize="small" /> : <CollapsedIcon fontSize="small" />}
                                </IconButton>
                              ) : null}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap title={row.itemName ?? row.inventoryItemId}>
                                  {row.itemName ?? `(deleted item ${row.inventoryItemId.slice(-6)})`}
                                </Typography>
                                {size && <Typography variant="caption" color="text.secondary" noWrap display="block">{size}</Typography>}
                                {splittable && !isOpen && (
                                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                                    {row.byLocation.length} locations
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                        );
                      case 'category':
                        return (
                          <TableCell key="category" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Typography variant="body2" color="text.secondary" noWrap title={`${row.category || '—'}${row.subcategory ? ` / ${row.subcategory}` : ''}`}>
                              {row.category || '—'}{row.subcategory ? ` / ${row.subcategory}` : ''}
                            </Typography>
                          </TableCell>
                        );
                      case 'opening':         return qtyCell('opening', row.openingQuantity);
                      case 'received':        return qtyCell('received', row.received, { dim: true });
                      case 'transferIn':      return qtyCell('transferIn', row.transferIn, { dim: true });
                      case 'adjustmentPlus':  return qtyCell('adjustmentPlus', row.adjustmentPlus, { dim: true });
                      case 'salesDepletion':  return qtyCell('salesDepletion', row.salesDepletion, { dim: true });
                      case 'waste':           return qtyCell('waste', row.waste, { dim: true });
                      case 'transferOut':     return qtyCell('transferOut', row.transferOut, { dim: true });
                      case 'adjustmentMinus': return qtyCell('adjustmentMinus', row.adjustmentMinus, { dim: true });
                      case 'reversalVoid':    return qtyCell('reversalVoid', row.reversalVoid, { dim: true });
                      case 'net':
                        return (
                          <TableCell
                            key="net"
                            align="right"
                            sx={{
                              whiteSpace: 'nowrap',
                              fontWeight: 600,
                              color: row.netChange > 0 ? '#1a7f37' : row.netChange < 0 ? '#cf222e' : theme.palette.text.disabled,
                            }}
                          >
                            {row.netChange > 0 ? `+${formatQty(row.netChange)}` : formatQty(row.netChange)}
                          </TableCell>
                        );
                      case 'closing':
                        return <TableCell key="closing" align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{formatQty(row.closingQuantity)}</TableCell>;
                      case 'supplier':
                        return (
                          <TableCell key="supplier" sx={{ whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.supplierName ?? undefined}>
                            {row.supplierName ?? '—'}
                          </TableCell>
                        );
                      case 'unitCost':
                        return <TableCell key="unitCost" align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtMoney(fromMinorUnitsOrNull(row.unitCost))}</TableCell>;
                      case 'salePrice':
                        return <TableCell key="salePrice" align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtMoney(fromMinorUnitsOrNull(row.salePrice))}</TableCell>;
                      case 'closingValue':
                        return <TableCell key="closingValue" align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtMoney(fromMinorUnitsOrNull(row.closingValue))}</TableCell>;
                      default:
                        return <TableCell key={col.key} />;
                    }
                  };
                  return (
                    <React.Fragment key={row.inventoryItemId}>
                      <TableRow hover>{activeColumns.map(renderCell)}</TableRow>
                      {isOpen && row.byLocation.map(loc => locationSubRow(row.inventoryItemId, loc))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <InventoryMovementsReportHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      <CostTrackingHelpModal open={costHelpOpen} onClose={() => setCostHelpOpen(false)} />
    </Box>
  );
};

export default InventoryMovementsReport;
